"use client";

import { useRef } from "react";
import Link from "next/link";
import { Container, SectionTitle, BodyText } from "./Primitives";
import { sortEventsDesc, type CompanyEventsBlock } from "@/lib/companySettings";

/**
 * Events — горизонтальная карусель мероприятий на главной.
 *
 * Только обычные статьи (events.articles → /events/{slug}), newest-first.
 * Промо-страница (current_event) сюда НЕ попадает — у неё свой вход через
 * плашку в шапке. Если статей нет — секция не рендерится.
 */
export default function Events({
  events,
}: {
  events?: CompanyEventsBlock;
} = {}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const block = events ?? {};
  const articles = sortEventsDesc((block.articles ?? []).filter((a) => a.slug && a.title))
    .map((a) => ({ title: a.title, cardDate: a.cardDate || a.date, image: a.image, href: `/events/${a.slug}` }));

  function scrollBy(direction: "prev" | "next") {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>("[data-card]");
    if (!card) return;
    const gap = 20;
    const delta = (card.offsetWidth + gap) * (direction === "next" ? 1 : -1);
    el.scrollBy({ left: delta, behavior: "smooth" });
  }

  if (articles.length === 0) return null;

  return (
    <section id="events" className="bg-white py-14 sm:py-16 lg:py-[85px]">
      <Container>
        <div className="mb-10 grid gap-6 sm:mb-12 lg:grid-cols-[minmax(220px,400px)_1fr] lg:gap-[120px] lg:mb-[50px]">
          <SectionTitle>{block.title || "Мероприятия"}</SectionTitle>
          <div className="flex flex-col gap-6">
            {block.lead && <BodyText>{block.lead}</BodyText>}
            <div className="hidden justify-end gap-3 sm:flex">
              <CarouselButton onClick={() => scrollBy("prev")} label="Предыдущее" direction="prev" />
              <CarouselButton onClick={() => scrollBy("next")} label="Следующее" direction="next" />
            </div>
          </div>
        </div>
      </Container>

      <Container>
        <div className="relative mb-10 lg:mb-12">
          <div
            ref={scrollerRef}
            className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {articles.map((a, i) => (
              <Link
                key={`${a.href}-${i}`}
                href={a.href}
                data-card
                className="group flex shrink-0 snap-start flex-col gap-4 w-[280px] sm:w-[360px] lg:w-[calc((100%-40px)/3)] lg:gap-5"
              >
                <div className="relative aspect-[16/10] overflow-hidden rounded-[20px]">
                  {a.image && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={a.image}
                      alt={a.title}
                      className="absolute inset-0 size-full object-cover"
                    />
                  )}
                </div>
                <h3 className="text-[18px] font-medium leading-[1.3] tracking-[-0.3px] text-black transition-colors group-hover:text-primary sm:text-[20px] lg:text-[22px]">
                  {a.title}
                </h3>
                {/* mt-auto прижимает дату к низу — все даты на одном уровне
                    независимо от числа строк в заголовке (без «лесенки»). */}
                <p className="mt-auto text-[14px] text-text-muted sm:text-[15px] lg:text-[16px]">
                  {a.cardDate}
                </p>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex justify-center">
          <Link
            href="/events"
            className="inline-flex items-center gap-2 rounded-[70px] bg-primary px-6 py-3.5 text-[15px] font-medium text-white transition hover:bg-primary-hover sm:px-8 sm:py-4 sm:text-base"
          >
            <span>Все мероприятия</span>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/img/tagjs/yd1xihum.png" alt="" width={16} height={16} className="rounded-full" />
          </Link>
        </div>
      </Container>
    </section>
  );
}

function CarouselButton({
  onClick,
  label,
  direction,
}: {
  onClick: () => void;
  label: string;
  direction: "prev" | "next";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex size-11 items-center justify-center rounded-full border border-black/15 bg-white text-black transition-all hover:border-black hover:-translate-y-0.5 hover:shadow-md"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={direction === "prev" ? "rotate-180" : ""}
      >
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  );
}
